// @section 1 Acquisitions shell — tabbed sub-routes.
//   /acquisitions             → Pipeline (default)
//   /acquisitions/cim         → CIM Explorer (port of CIM-EXPLORER-2026-06-02.html)
//   /acquisitions/queue       → Call Queue (open tasks Patrick + Adam owe today)
//   /acquisitions/:dealId     → AcquisitionDetail (per-deal module canvas)
//
// The tab strip hides on deal-detail routes so the per-deal header owns chrome.

import { NavLink, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AcquisitionsProvider, useAcquisitions } from '../../lib/AcquisitionsContext';
import { useIsMobile } from '../../lib/useIsMobile';
import PipelineView from './PipelineView';
import CimExplorer from './CimExplorer';
import CallQueue from './CallQueue';
import AcquisitionDetail from '../AcquisitionDetail';

const TABS: { to: string; label: string; end?: boolean }[] = [
  { to: '/acquisitions',       label: 'Pipeline',     end: true },
  { to: '/acquisitions/cim',   label: 'CIM Explorer' },
  { to: '/acquisitions/queue', label: 'Call Queue' },
];

function AcquisitionsTabs() {
  const loc = useLocation();
  const acq = useAcquisitions();
  // @block 1.1 On phones the strip wraps and tightens its gutters so the
  // crumb + 3 tabs + freshness stamp don't overflow (the canvas clips overflow-x).
  const isMobile = useIsMobile();
  const onTab = TABS.some((t) => (t.end ? loc.pathname === t.to : loc.pathname.startsWith(t.to)));
  if (!onTab) return null;
  return (
    <div style={{
      display: 'flex', gap: 4, padding: isMobile ? '10px 14px 0' : '12px 26px 0',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      borderBottom: '1px solid var(--t2-border)',
      background: 'rgba(10,19,34,0.35)',
      flexShrink: 0,
    }}>
      <div className="crumb" style={{ marginRight: 16, alignSelf: 'center', marginBottom: 8 }}>
        <b>Atlas</b><span className="sep">›</span><b>Acquisitions</b>
      </div>
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          style={({ isActive }) => ({
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 600,
            color: isActive ? 'var(--tx-0)' : 'var(--tx-3)',
            textDecoration: 'none',
            borderBottom: isActive ? '2px solid var(--brand-3)' : '2px solid transparent',
            marginBottom: -1,
          })}
        >
          {t.label}
        </NavLink>
      ))}
      <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--tx-4)', alignSelf: 'center', marginBottom: 8 }}>
        {acq.loading ? 'loading…' : acq.source === 'api' ? 'live' : 'fixture'}
        {' · '}{new Date(acq.asOf).toLocaleString()}
      </span>
    </div>
  );
}

// AcquisitionDetail expects useParams<{ dealId }>; we proxy here so it can
// stay in routes/AcquisitionDetail.tsx (already exists) without changes.
function DetailProxy() {
  const { dealId } = useParams();
  if (!dealId) return null;
  return <AcquisitionDetail key={dealId} />;
}

export default function AcquisitionsShell() {
  return (
    <AcquisitionsProvider>
      <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', minHeight: 0 }}>
        <AcquisitionsTabs />
        <div style={{ minHeight: 0, overflow: 'hidden' }}>
          <Routes>
            <Route index element={<PipelineView />} />
            <Route path="cim"   element={<CimExplorer />} />
            <Route path="queue" element={<CallQueue />} />
            <Route path=":dealId" element={<DetailProxy />} />
          </Routes>
        </div>
      </div>
    </AcquisitionsProvider>
  );
}
